import React, { JSX } from 'react';
import SEO from 'components/seo';
import {
	Box,
	Container,
	VStack,
	Text,
	Heading,
	Flex,
	Icon,
	Badge,
	HStack,
	Circle,
	Grid,
	GridItem,
} from '@chakra-ui/react';
import { ROUTES } from 'services/shared/routerService';
import CtaButton from 'components/CtaButton';
import FAQSection from 'components/faqSection';
import { motion } from 'framer-motion';
import { ArrowRight, Sparkles, Download, MousePointer2, Zap, Wand2, CheckCircle2, ProportionsIcon } from 'lucide-react';
import ScrollReveal from 'components/ScrollReveal';
import Image from 'next/image';
import HeroSection from 'components/heroSection';
import HeroVisual, { HeroVisualItem } from 'components/heroVisual';
import { GAME_ASSET } from 'services/shared/imageService';

const MotionBox = motion.create(Box);

const HERO_VISUAL_ITEMS: HeroVisualItem[] = [
	{
		type: 'image',
		src: GAME_ASSET.CHARACTER1,
		x: 280,
		y: -180,
		rotate: 12,
		delay: 0.2,
		width: '120px',
		height: '160px',
	},
	{
		type: 'image',
		src: GAME_ASSET.CHARACTER_MOVEMENT,
		x: -280,
		y: -200,
		rotate: -15,
		delay: 0.1,
		width: '120px',
		height: '160px',
	},
	{
		type: 'image',
		src: GAME_ASSET.TEXTURE_DIRT,
		x: -200,
		y: 120,
		rotate: -10,
		delay: 0.3,
		width: '120px',
		height: '160px',
	},
	{
		type: 'image',
		src: GAME_ASSET.ENEMY,
		x: -320,
		y: -40,
		rotate: -8,
		delay: 0.7,
		width: '120px',
		height: '160px',
	},
	{
		type: 'image',
		src: GAME_ASSET.SWORDS_SPRITES,
		x: 360,
		y: 180,
		rotate: 8,
		delay: 0.4,
		width: '120px',
		height: '160px',
	},
	{
		type: 'image',
		src: GAME_ASSET.GAME_BACKGROUND,
		x: 50,
		y: -280,
		rotate: 5,
		delay: 0.5,
		hideLg: true,
		width: '250px',
		height: '160px',
	},
	{
		type: 'image',
		src: GAME_ASSET.HEAL_POTION,
		x: 100,
		y: 280,
		rotate: -5,
		delay: 0.6,
		width: '120px',
		height: '160px',
	},
	{
		type: 'image',
		src: GAME_ASSET.HOUSE_MODEL,
		x: 300,
		y: 40,
		rotate: 10,
		delay: 0.8,
		width: '120px',
		height: '160px',
	},
];

// ─── Character cards for Step 2 ───────────────────────────────────────────────

const CHARACTER_IMGS = [
	{
		src: GAME_ASSET.POTION,
		rotate: -8,
		x: -110,
		y: 20,
		delay: 0.15,
		zIndex: 1,
	},
	{
		src: GAME_ASSET.ANIMAL,
		rotate: 3,
		x: 0,
		y: -10,
		delay: 0.05,
		zIndex: 3,
	},
	{
		src: GAME_ASSET.GHOST,
		rotate: 10,
		x: 110,
		y: 30,
		delay: 0.25,
		zIndex: 2,
	},
];

const FloatingCharacterCards = () => (
	<Box position="relative" h="340px" w="full" display="flex" alignItems="center" justifyContent="center">
		{/* Ambient glow under cards */}
		<Box
			position="absolute"
			w="260px"
			h="260px"
			borderRadius="full"
			bg="blue.100"
			filter="blur(60px)"
			opacity={0.5}
			zIndex={0}
		/>
		{CHARACTER_IMGS.map((img, i) => (
			<MotionBox
				key={i}
				initial={{ opacity: 0, y: 40, rotate: 0 }}
				animate={{
					opacity: 1,
					x: img.x,
					y: [img.y, img.y - 10, img.y],
					rotate: img.rotate,
				}}
				transition={{
					opacity: { duration: 0.5, delay: img.delay },
					x: { duration: 0.6, delay: img.delay, ease: 'easeOut' },
					rotate: { duration: 0.6, delay: img.delay },
					y: { duration: 4 + i * 0.5, repeat: Infinity, ease: 'easeInOut', delay: img.delay + 0.6 },
				}}
				position="absolute"
				zIndex={img.zIndex}
				w="160px"
				h="220px"
				bg="white"
				borderRadius="20px"
				shadow="2xl"
				borderWidth="1.5px"
				borderColor="gray.100"
				overflow="hidden"
				display="flex"
				alignItems="center"
				justifyContent="center"
				p={2}
			>
				{/* checkered bg to show transparency */}
				<Box
					position="absolute"
					inset={0}
					borderRadius="18px"
					style={{
						backgroundImage:
							'linear-gradient(45deg,#f0f0f0 25%,transparent 25%),linear-gradient(-45deg,#f0f0f0 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#f0f0f0 75%),linear-gradient(-45deg,transparent 75%,#f0f0f0 75%)',
						backgroundSize: '16px 16px',
						backgroundPosition: '0 0,0 8px,8px -8px,-8px 0',
					}}
					opacity={0.4}
				/>
				<Box position="relative" w="full" h="full">
					<Image src={img.src} alt={`Character ${i + 1}`} fill style={{ objectFit: 'contain' }} />
				</Box>
			</MotionBox>
		))}
	</Box>
);

function AIGameAssetsGenerator(): JSX.Element {
	return (
		<Box bg="white">
			<SEO
				title="AI Game Assets Generator - Create Game Textures, Sprites & Pixel Art"
				description="Generate Free AI Game Assets with our AI game asset creator. Create textures, sprites, pixel art and more with our AI game sprite generator. Try for free!"
			/>

			<HeroSection
				copy={`New • AI Game Asset Generator`}
				title={`**Generate Free AI** \n Game Assets`}
				subtitle={`Create stunning game textures, sprites, characters, and pixel art with our AI game asset maker. Generate, export and drag into any game engine or platform instantly.`}
				ctaPrimary={{ text: 'Start creating — Free', href: ROUTES.REGISTER }}
				content={<HeroVisual items={HERO_VISUAL_ITEMS} />}
			/>

			{/* ── HOW IT WORKS — 3 big steps ── */}
			<Box py={{ base: 20, lg: 32 }} bg="white">
				<Container maxW="7xl" px={{ base: 6, lg: 12 }}>
					{/* Section header */}
					<ScrollReveal>
						<VStack gap={3} mb={{ base: 16, lg: 20 }} align="start">
							<Text
								fontSize="11px"
								fontWeight={600}
								textTransform="uppercase"
								letterSpacing="0.18em"
								color="gray.400"
							>
								How it works
							</Text>
							<Heading
								fontSize={{ base: '3rem', md: '4.5rem', lg: '5.5rem' }}
								fontWeight={800}
								lineHeight={0.95}
								letterSpacing="-0.04em"
								color="gray.900"
							>
								Generate,
								<br />
								<Box as="span" color="gray.400">
									export, play.
								</Box>
							</Heading>
							<Text
								fontSize={{ base: '16px', md: '18px' }}
								color="gray.500"
								maxW="xl"
								lineHeight={1.7}
								mt={2}
							>
								From text prompt to game-ready asset. No design experience needed.
							</Text>
						</VStack>
					</ScrollReveal>

					{/* ── Step 1 ── */}
					<ScrollReveal>
						<Box mb={{ base: 24, lg: 32 }}>
							{/* Asymmetric: number big on left, content offset right */}
							<Flex
								direction={{ base: 'column', lg: 'row' }}
								gap={{ base: 10, lg: 0 }}
								align={{ lg: 'flex-end' }}
							>
								{/* Left — giant number */}
								<Box flex="0 0 auto" w={{ lg: '280px' }} pr={{ lg: 12 }}>
									<Text
										fontSize={{ base: '120px', lg: '180px' }}
										fontWeight={900}
										lineHeight={1}
										color="gray.100"
										letterSpacing="-0.06em"
										userSelect="none"
									>
										01
									</Text>
								</Box>
								{/* Right — text + prompt mockup */}
								<Box flex={1}>
									<HStack gap={3} mb={4}>
										<Circle size="10" bg="gray.900">
											<Icon as={Wand2} boxSize={5} color="white" />
										</Circle>
										<Text
											fontSize="13px"
											fontWeight={700}
											color="gray.400"
											letterSpacing="0.1em"
											textTransform="uppercase"
										>
											Step one
										</Text>
									</HStack>
									<Heading
										fontSize={{ base: '2.2rem', md: '3rem' }}
										fontWeight={800}
										letterSpacing="-0.03em"
										color="gray.900"
										mb={4}
										lineHeight={1.1}
									>
										Describe your asset
									</Heading>
									<Text
										fontSize={{ base: '16px', md: '18px' }}
										color="gray.500"
										lineHeight={1.8}
										maxW="2xl"
										mb={8}
									>
										Type any description in plain language. The more specific you are, the better
										the result.
									</Text>
									{/* Prompt mockup */}
									<Box
										bg="gray.50"
										borderWidth="1.5px"
										borderColor="gray.200"
										borderRadius="2xl"
										p={5}
										maxW="540px"
									>
										<Text
											fontSize="11px"
											fontWeight={600}
											color="gray.400"
											textTransform="uppercase"
											letterSpacing="0.1em"
											mb={3}
										>
											Your prompt
										</Text>
										<Flex
											align="center"
											gap={3}
											bg="white"
											borderWidth="1px"
											borderColor="gray.200"
											borderRadius="xl"
											p={4}
										>
											<Icon as={Sparkles} boxSize={5} color="blue.400" flexShrink={0} />
											<Text fontSize="15px" color="gray.700" fontWeight={500}>
												"2D pixel art heal potion"
											</Text>
										</Flex>
										<HStack gap={2} mt={3} flexWrap="wrap">
											{['Fantasy', 'Pixel Art', 'Side View', 'Transparent'].map((tag) => (
												<Badge
													key={tag}
													bg="white"
													borderWidth="1px"
													borderColor="gray.200"
													color="gray.600"
													fontSize="11px"
													fontWeight={600}
													px={3}
													py={1}
													borderRadius="full"
												>
													{tag}
												</Badge>
											))}
										</HStack>
									</Box>
								</Box>
							</Flex>
						</Box>
					</ScrollReveal>

					{/* ── Step 2 ── */}
					<ScrollReveal>
						<Box mb={{ base: 24, lg: 32 }}>
							<Flex
								direction={{ base: 'column', lg: 'row' }}
								gap={{ base: 10, lg: 0 }}
								align={{ lg: 'flex-start' }}
							>
								{/* Left — number pushed right */}
								<Box
									flex="0 0 auto"
									w={{ lg: '280px' }}
									pr={{ lg: 12 }}
									display="flex"
									flexDir="column"
									alignItems={{ lg: 'flex-end' }}
								>
									<Text
										fontSize={{ base: '120px', lg: '180px' }}
										fontWeight={900}
										lineHeight={1}
										color="gray.100"
										letterSpacing="-0.06em"
										userSelect="none"
										textAlign={{ lg: 'right' }}
									>
										02
									</Text>
								</Box>

								{/* Right — text + floating character cards */}
								<Box flex={1}>
									<HStack gap={3} mb={4}>
										<Circle size="10" bg="gray.900">
											<Icon as={Zap} boxSize={5} color="white" />
										</Circle>
										<Text
											fontSize="13px"
											fontWeight={700}
											color="gray.400"
											letterSpacing="0.1em"
											textTransform="uppercase"
										>
											Step two
										</Text>
									</HStack>
									<Heading
										fontSize={{ base: '2.2rem', md: '3rem' }}
										fontWeight={800}
										letterSpacing="-0.03em"
										color="gray.900"
										mb={4}
										lineHeight={1.1}
									>
										Generate & Download
									</Heading>
									<Text
										fontSize={{ base: '16px', md: '18px' }}
										color="gray.500"
										lineHeight={1.8}
										maxW="2xl"
										mb={10}
									>
										Our AI creates high-quality, transparent 2D characters, sprites and textures.{' '}
										<br /> Download in PNG — crisp, clean and ready to use.
									</Text>

									{/* Floating character cards visual */}
									<Box position="relative">
										<FloatingCharacterCards />

										{/* Speed badge */}
										<MotionBox
											initial={{ opacity: 0, y: 10 }}
											animate={{ opacity: 1, y: 0 }}
											transition={{ delay: 0.8 }}
											position="absolute"
											top={4}
											right={4}
											bg="white"
											borderRadius="14px"
											shadow="xl"
											p={3}
											borderWidth="1px"
											borderColor="gray.100"
											zIndex={20}
										>
											<HStack gap={2}>
												<Circle size="8" bg="green.50">
													<Icon as={ProportionsIcon} boxSize={4} color="green.500" />
												</Circle>
												<VStack gap={0} align="start">
													<Text
														fontSize="13px"
														fontWeight={800}
														color="gray.900"
														lineHeight={1}
													>
														16:9, 9:16, 1:1
													</Text>
													<Text fontSize="10px" color="gray.500" lineHeight={1.3}>
														Aspect Ratio
													</Text>
												</VStack>
											</HStack>
										</MotionBox>

										{/* Format badge */}
										<MotionBox
											initial={{ opacity: 0, y: 10 }}
											animate={{ opacity: 1, y: 0 }}
											transition={{ delay: 1 }}
											position="absolute"
											bottom={4}
											left={4}
											bg="white"
											borderRadius="14px"
											shadow="xl"
											p={3}
											borderWidth="1px"
											borderColor="gray.100"
											zIndex={20}
										>
											<HStack gap={2}>
												<Circle size="8" bg="blue.50">
													<Icon as={Download} boxSize={4} color="blue.500" />
												</Circle>
												<VStack gap={0} align="start">
													<Text
														fontSize="11px"
														fontWeight={700}
														color="gray.900"
														lineHeight={1.2}
													>
														PNG • Transparent
													</Text>
													<Text fontSize="10px" color="gray.500" lineHeight={1.3}>
														Game-engine ready
													</Text>
												</VStack>
											</HStack>
										</MotionBox>
									</Box>
								</Box>
							</Flex>
						</Box>
					</ScrollReveal>

					{/* ── Step 3 ── */}
					<ScrollReveal>
						<Box>
							<Flex
								direction={{ base: 'column', lg: 'row' }}
								gap={{ base: 10, lg: 0 }}
								align={{ lg: 'flex-end' }}
							>
								{/* Left — giant number */}
								<Box flex="0 0 auto" w={{ lg: '280px' }} pr={{ lg: 12 }}>
									<Text
										fontSize={{ base: '120px', lg: '180px' }}
										fontWeight={900}
										lineHeight={1}
										color="gray.100"
										letterSpacing="-0.06em"
										userSelect="none"
									>
										03
									</Text>
								</Box>

								{/* Right — text + engine mockup */}
								<Box flex={1}>
									<HStack gap={3} mb={4}>
										<Circle size="10" bg="gray.900">
											<Icon as={MousePointer2} boxSize={5} color="white" />
										</Circle>
										<Text
											fontSize="13px"
											fontWeight={700}
											color="gray.400"
											letterSpacing="0.1em"
											textTransform="uppercase"
										>
											Step three
										</Text>
									</HStack>
									<Heading
										fontSize={{ base: '2.2rem', md: '3rem' }}
										fontWeight={800}
										letterSpacing="-0.03em"
										color="gray.900"
										mb={4}
										lineHeight={1.1}
									>
										Drag into your game
									</Heading>
									<Text
										fontSize={{ base: '16px', md: '18px' }}
										color="gray.500"
										lineHeight={1.8}
										maxW="2xl"
										mb={8}
									>
										Assets are exported and optimised for every major game engine. Just drag and
										drop — no post-processing, no format conversion, no headaches.
									</Text>

									{/* Engine pills */}
									<Flex wrap="wrap" gap={3} mb={8}>
										{['Unity', 'Unreal Engine', 'Godot', 'GameMaker', 'Phaser', 'Construct 3'].map(
											(engine) => (
												<Box
													key={engine}
													px={5}
													py={2.5}
													bg="gray.50"
													borderWidth="1.5px"
													borderColor="gray.200"
													borderRadius="full"
													fontSize="14px"
													fontWeight={600}
													color="gray.700"
												>
													{engine}
												</Box>
											)
										)}
									</Flex>

									<CtaButton href={ROUTES.REGISTER} hoverScale shadow="lg">
										Start for free
										<Icon as={ArrowRight} ml={1} boxSize={4} />
									</CtaButton>
								</Box>
							</Flex>
						</Box>
					</ScrollReveal>
				</Container>
			</Box>

			{/* ── Features grid — asymmetric bento ── */}
			<Box py={{ base: 20, lg: 28 }} bg="gray.50">
				<Container maxW="7xl" px={{ base: 6, lg: 12 }}>
					<ScrollReveal>
						<VStack gap={3} mb={14} align="start">
							<Text
								fontSize="11px"
								fontWeight={600}
								textTransform="uppercase"
								letterSpacing="0.18em"
								color="gray.400"
							>
								What you can create
							</Text>
							<Heading
								fontSize={{ base: '2.5rem', md: '3.5rem' }}
								fontWeight={800}
								letterSpacing="-0.04em"
								color="gray.900"
								lineHeight={1.05}
							>
								Every asset your
								<br />
								game needs
							</Heading>
						</VStack>
					</ScrollReveal>

					{/* Bento-style asymmetric grid */}
					<Grid templateColumns={{ base: '1fr', md: 'repeat(12, 1fr)' }} templateRows="auto" gap={4}>
						{/* Wide card — texture */}
						<GridItem colSpan={{ base: 1, md: 7 }}>
							<ScrollReveal>
								<Box
									bg="white"
									borderRadius="2xl"
									borderWidth="1px"
									borderColor="gray.100"
									shadow="sm"
									overflow="hidden"
									_hover={{ shadow: 'lg', transform: 'translateY(-3px)' }}
									transition="all 0.25s"
								>
									<Box h="260px" position="relative" overflow="hidden">
										<Image
											src={GAME_ASSET.HOUSE}
											alt="Textures"
											fill
											style={{ objectFit: 'cover' }}
										/>
										<Box
											position="absolute"
											inset={0}
											bgGradient="to-t"
											gradientFrom="black/30"
											gradientTo="transparent"
										/>
									</Box>
									<Box p={7}>
										<Heading fontSize="20px" fontWeight={700} color="gray.900" mb={2}>
											AI Texture Generator
										</Heading>
										<Text fontSize="14px" color="gray.500" lineHeight={1.7}>
											Create seamless, tileable game textures for any surface — stone, wood,
											grass, metal, sci-fi, or fantasy. Any style, any resolution, instant
											download.
										</Text>
									</Box>
								</Box>
							</ScrollReveal>
						</GridItem>

						{/* Tall card — sprite */}
						<GridItem colSpan={{ base: 1, md: 5 }} rowSpan={{ md: 2 }}>
							<ScrollReveal>
								<Box
									bg="white"
									borderRadius="2xl"
									borderWidth="1px"
									borderColor="gray.100"
									shadow="sm"
									overflow="hidden"
									h="full"
									_hover={{ shadow: 'lg', transform: 'translateY(-3px)' }}
									transition="all 0.25s"
								>
									<Box h="220px" position="relative" overflow="hidden">
										<Image
											src={GAME_ASSET.TREES}
											alt="Sprites"
											fill
											style={{ objectFit: 'cover' }}
										/>
										<Box
											position="absolute"
											inset={0}
											bgGradient="to-t"
											gradientFrom="black/30"
											gradientTo="transparent"
										/>
									</Box>
									<Box p={6}>
										<Heading fontSize="18px" fontWeight={700} color="gray.900" mb={2}>
											AI Sprite Generator
										</Heading>
										<Text fontSize="13px" color="gray.500" lineHeight={1.7} mb={4}>
											Generate animated sprite sheets for characters, enemies, items, and effects.
											Perfect for 2D side-scrollers and RPGs.
										</Text>
										<VStack align="start" gap={2}>
											{[
												'Transparent PNG output',
												'Multiple poses & animations',
												'Any art style',
											].map((f) => (
												<HStack key={f} gap={2}>
													<Icon as={CheckCircle2} boxSize={4} color="green.500" />
													<Text fontSize="12px" color="gray.600" fontWeight={500}>
														{f}
													</Text>
												</HStack>
											))}
										</VStack>
									</Box>
								</Box>
							</ScrollReveal>
						</GridItem>

						{/* Small card — pixel art */}
						<GridItem colSpan={{ base: 1, md: 4 }}>
							<ScrollReveal>
								<Box
									bg="white"
									borderRadius="2xl"
									borderWidth="1px"
									borderColor="gray.100"
									shadow="sm"
									overflow="hidden"
									_hover={{ shadow: 'lg', transform: 'translateY(-3px)' }}
									transition="all 0.25s"
								>
									<Box h="160px" position="relative" overflow="hidden">
										<Image
											src={GAME_ASSET.ISLANDS}
											alt="Pixel Art"
											fill
											style={{ objectFit: 'cover' }}
										/>
									</Box>
									<Box p={5}>
										<Heading fontSize="16px" fontWeight={700} color="gray.900" mb={1.5}>
											AI Pixel Art Generator
										</Heading>
										<Text fontSize="13px" color="gray.500" lineHeight={1.6}>
											Authentic pixel art for retro games and indie projects. Every pixel
											precisely placed.
										</Text>
									</Box>
								</Box>
							</ScrollReveal>
						</GridItem>

						{/* Small card — characters */}
						<GridItem colSpan={{ base: 1, md: 3 }}>
							<ScrollReveal>
								<Box
									bg="white"
									borderRadius="2xl"
									borderWidth="1px"
									borderColor="gray.100"
									shadow="sm"
									overflow="hidden"
									_hover={{ shadow: 'lg', transform: 'translateY(-3px)' }}
									transition="all 0.25s"
								>
									<Box h="160px" position="relative" overflow="hidden">
										<Image
											src={GAME_ASSET.CHARACTERS}
											alt="Characters"
											fill
											style={{ objectFit: 'cover' }}
										/>
									</Box>
									<Box p={5}>
										<Heading fontSize="16px" fontWeight={700} color="gray.900" mb={1.5}>
											Character Design
										</Heading>
										<Text fontSize="13px" color="gray.500" lineHeight={1.6}>
											Heroes, villains, NPCs — generate character sprites and concept art in
											seconds.
										</Text>
									</Box>
								</Box>
							</ScrollReveal>
						</GridItem>
					</Grid>
				</Container>
			</Box>

			{/* ── Asset Library showcase — intentionally asymmetric ── */}
			<Box py={{ base: 20, lg: 28 }}>
				<Container maxW="7xl" px={{ base: 6, lg: 12 }}>
					{/* Offset heading */}
					<ScrollReveal>
						<Flex direction={{ base: 'column', lg: 'row' }} gap={16} align="flex-start" mb={16}>
							<Box flex="0 0 auto" w={{ lg: '340px' }}>
								<Text
									fontSize="11px"
									fontWeight={600}
									textTransform="uppercase"
									letterSpacing="0.18em"
									color="gray.400"
									mb={3}
								>
									Asset Library
								</Text>
								<Heading
									fontSize={{ base: '2.5rem', md: '3.5rem' }}
									fontWeight={800}
									letterSpacing="-0.04em"
									color="gray.900"
									lineHeight={1.05}
								>
									All your assets, one place
								</Heading>
							</Box>
							<Box pt={{ lg: 10 }} flex={1}>
								<Text fontSize={{ base: '16px', md: '18px' }} color="gray.500" lineHeight={1.8} mb={8}>
									Your account studio keeps every asset organised by project. Edit generations, build
									collections, and export them when needed.
								</Text>
								<VStack align="start" gap={3} mb={8}>
									{[
										'10GB free asset storage',
										'One-click export to major game engines',
										'Commercial licence included',
									].map((item) => (
										<HStack key={item} gap={3}>
											<Icon as={CheckCircle2} boxSize={5} color="green.500" />
											<Text fontSize="15px" fontWeight={500} color="gray.700">
												{item}
											</Text>
										</HStack>
									))}
								</VStack>
								<CtaButton href={ROUTES.REGISTER} hoverScale shadow="lg">
									Try it free
									<Icon as={ArrowRight} ml={1} boxSize={4} />
								</CtaButton>
							</Box>
						</Flex>
					</ScrollReveal>

					{/* Asymmetric 5-image mosaic */}
					<ScrollReveal>
						<Box w={'100%'} h={400} position={'relative'} borderRadius={3}>
							<Image
								src={GAME_ASSET.ICONSET}
								alt="Iconset"
								fill
								style={{ objectFit: 'cover', width: '100%', height: '100%', borderRadius: 16 }}
							/>
						</Box>
					</ScrollReveal>
				</Container>
			</Box>

			{/* ── CTA ── */}
			<Box py={{ base: 20, lg: 28 }} bg="gray.900">
				<Container maxW="3xl">
					<VStack textAlign="center" gap={6}>
						<Badge
							bg="white/10"
							color="white"
							fontSize="11px"
							fontWeight={600}
							px={4}
							py={1.5}
							borderRadius="full"
							letterSpacing="0.1em"
							textTransform="uppercase"
						>
							Get started free
						</Badge>
						<Heading
							fontSize={{ base: '2rem', md: '3.5rem' }}
							fontWeight={800}
							lineHeight={1.1}
							letterSpacing="-0.04em"
							color="white"
						>
							Start creating game assets today
						</Heading>
						<Text fontSize="17px" color="gray.400" maxW="xl" lineHeight={1.7}>
							Join thousands of game developers using our AI to create custom assets. No design skills
							needed.
						</Text>
						<CtaButton href={ROUTES.REGISTER} hoverScale shadow="2xl">
							Generate assets free
							<Icon as={ArrowRight} ml={1} boxSize={4} />
						</CtaButton>
						<Text fontSize="12px" color="gray.600">
							Free plan available · No credit card required · Commercial licence included
						</Text>
					</VStack>
				</Container>
			</Box>

			{/* ── FAQ ── */}
			<FAQSection
				faqs={[
					{
						question: 'What is an AI game assets generator?',
						answer: 'An AI game assets generator uses artificial intelligence to create game-related content like textures, sprites, characters and pixel art from text descriptions. It helps game developers create assets faster without needing artistic skills.',
					},
					{
						question: 'Can I use the generated game assets commercially?',
						answer: 'Yes, all game assets generated with our AI game asset creator can be used for commercial purposes in your games. You own the rights to use them in your projects.',
					},
					{
						question: 'What file formats are supported for export?',
						answer: 'You can export your AI-generated game assets in PNG. The assets are optimized.',
					},
					{
						question: 'How does the AI game sprite generator work?',
						answer: 'Simply describe the sprite you want in natural language (e.g., "a pixel art sprite of a wizard character with a blue robe"), and our AI will generate a variation for you.',
					},
					{
						question: 'Is there a free trial available?',
						answer: 'Yes, we offer a free plan that lets you generate a limited number of game assets. You can upgrade to a paid plan for unlimited generations and advanced features.',
					},
				]}
			/>
		</Box>
	);
}

export default AIGameAssetsGenerator;
